import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomFilter } from 'src/app/models/common/custom-filter';
import { List } from 'src/app/models/common/list.model';
import { OptionsList } from 'src/app/models/common/options-list.model';
import { SearchToolbar } from 'src/app/models/common/search-toolbar.model';
import { SortBy } from 'src/app/models/common/sort-by.model';
import {
  CustomFilterTable,
  CustomFilterTableChip
} from 'src/app/models/custom-filters/custom-filter-table.model';
import { BaseApiUrlService } from 'src/app/services/base-api-url.service';
import { CustomFiltersTabletService } from 'src/app/services/custom-filters-tablet.service';
import { SelectorOptions } from '../selector/selector-options';
import { CustomFilterComponent } from './custom-filter/custom-filter.component';
import { Tasks } from 'src/app/models/common/tasks.model';
import { SelectCardService } from 'src/app/services/select-card.service';

@Component({
    selector: 'app-toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ToolbarComponent implements OnInit {
  sortActive = false;
  criteria: FormControl = new FormControl('');
  @Input() permissions = {
    Edit: false,
    Delete: false,
    Create: false
  };

  @Input() title: string;

  //#region Variables de activar/desactivar inputs en toolbar
  @Input() key: string = 'Id';
  @Input() selectAll: boolean;
  @Input() activeSearchInput: boolean;
  @Input() activeTagsInput: boolean;
  @Input() activeGeneralTagsInput: boolean;
  @Input() onActiveFilterCustomInput: boolean;
  //#endregion

  //#region Variables para activar/desactivar botones en la vista de este componente,
  // según la necesidad */
  @Input() tagsBtn: boolean;
  @Input() sortBtn: boolean;
  @Input() searchBtn: boolean;
  @Input() gridTableBtn: boolean;
  @Input() sortBtnCardList = true;
  @Input() generalTagsBtn: boolean;
  @Input() toggleCardList: boolean;
  @Input() refreshBtn: boolean = true;
  @Input() syncIntegrationBtn: boolean;
  @Input() isSynchronizingIntegration?: boolean = false;
  @Input() customFilterTableBtn: boolean;
  @Input() customFiltersChips: CustomFilterTableChip[];
  //#endregion

  //#region Variables para agregar contenido a este componente
  private tags: any[] = [];
  get arkTags() {
    return this.tags;
  }
  @Input()
  set arkTags(allTags: any[]) {
    this.tags = allTags.map((tag) => ({ text: tag }));
  }
  @Input() sortBy: SortBy[] = [];
  @Input() options: OptionsList = {} as OptionsList;
  @Input() customFilter: CustomFilter = {} as CustomFilter;
  @Input() callToActionText: string;
  @Input() customFiltersTable: CustomFilterTable[];
  @Input() selectorOptions: SelectorOptions;
  @Input() integrationId?: number;

  //#region variables to trigger loader events*/
  @Input() autoLoadingMode = 'modeDefault';
  @Input() loading = false;
  @Input() activateCountdown: boolean;
  @Input() loadDuration: number;
  @Input() hideHoverBtn: boolean;
  @Input() apiComponent: string;
  @Input() _module: string;
  @Input() excludedMediaType: string;
  @Input() mediaType: string[];
  @Input() locationId: number;
  @Input() version: boolean;
  @Input() customFilterChipsBtn: boolean;
  @Input() customTooltipFilterChip: string = 'general.Filter';
  @Input() tasks: Tasks[];
  //#endregion

  //#region VARIABLES PARA EMITIR EVENTOS
  /** Envía el texto que se escribe en el input */
  @Output() searching: EventEmitter<SearchToolbar> = new EventEmitter();

  /** Sends the sync integration event */
  @Output() syncIntegration: EventEmitter<any> = new EventEmitter();

  /** Envía el cambio entre grid/tabla */
  @Output() toggledCardListActive: EventEmitter<boolean> = new EventEmitter();

  /** Envía el filtro/ordenamiento seleccionado */
  @Output() filterSort: EventEmitter<SortBy> = new EventEmitter();

  /** Envía la opción seleccionada */
  @Output() optionSelected: EventEmitter<string | number> = new EventEmitter();

  /** Envía el filtro personalizado seleccionado */
  @Output() selectedCustomFilter = new EventEmitter();

  /** To refresh the elements or page as needed */
  @Output() reloadContent = new EventEmitter();

  @Output() callToAction: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectorDevice: EventEmitter<any> = new EventEmitter<any>();
  //#endregion

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild(MatInput) searchInput: MatInput;

  @ViewChild('customFilterTable')
  customFilterTableComponent: CustomFilterComponent;

  @Input() set closeFilter(value: boolean) {
    this.customFilterTableComponent?.hideFilters();
  }
  filteredTagsOptions: SelectorOptions;
  filteredGeneralTagsOptions: SelectorOptions;
  filteredGeneralOptions: SelectorOptions;
  showFilterByChips: boolean;

  constructor(
    private readonly currentRoute: ActivatedRoute,
    private readonly translateService: TranslateService,
    public readonly customFiltersTableService: CustomFiltersTabletService,
    private readonly baseApiUrlService: BaseApiUrlService,
    public selectCardService: SelectCardService
  ) {}

  @HostListener('window:keyup', ['$event'])
  keyEsc(event: KeyboardEvent) {
    if (
      this.activeSearchInput &&
      event.key === 'Escape' &&
      this.searchInput.focused
    ) {
      this.activeSearchInput = false;
      this.onCriteriaClearAndEmit();
    }
  }

  ngOnInit() {
    if (this.tagsBtn) {
      this.initTags();
    }
    if (this.generalTagsBtn) {
      this.initGeneralTags();
      this.initGeneral();
    }
    this.getQueryParams();
    this.sortBy.forEach((option) => {
      if (
        ((option.Value === 'PublicId' ||
          option.Value === 'ProductId' ||
          option.Value === 'CategoryId') &&
          option.Direction === 'desc') ||
        (option.Value === 'City' && option.Direction === 'desc')
      ) {
        option.Active = true;
      } else {
        option.Active = false;
      }
    });
  }

  private getQueryParams() {
    this.currentRoute.queryParams.subscribe((params) => {
      if (params.criteria && params.criteria !== '') {
        this.criteria.setValue(params.criteria);
        this.activeSearchInput = true;
      }
      if (params.tags && params.tags !== '') {
        this.arkTags = params.tags;
        this.activeTagsInput = true;
        this.activeGeneralTagsInput = true;
      }
    });
  }

  /**
   * Mostrar/ocultar input de búsqueda
   */
  onActiveSearchInput() {
    this.activeSearchInput = !this.activeSearchInput;
    if (this.activeSearchInput) {
      setTimeout(() => {
        this.searchInput.focus();
      });
    } else {
      this.onCriteriaClearAndEmit();
    }
  }

  /**
   * Mostrar/ocultar input de búsqueda de tags
   * @param active Se envía true,
   * cuando se selecciona un tag de la lista
   */
  onActiveTags(active?: boolean) {
    this.activeTagsInput = !this.activeTagsInput;

    if (active) {
      this.activeTagsInput = active;
    } else {
      if (this.criteria.value === '' && this.arkTags.length === 0) {
        return;
      } else {
        this.arkTags = [];
        this.searching.emit({
          Criteria: this.criteria.value,
          Tags: this.arkTags
        });
      }
    }
  }

  onActiveTagsTabs(active?: boolean) {
    this.activeGeneralTagsInput = !this.activeGeneralTagsInput;

    if (active) {
      this.activeGeneralTagsInput = active;
    } else {
      if (this.criteria.value === '' && this.arkTags.length === 0) {
        return;
      } else {
        this.arkTags = [];
        this.searching.emit({
          Criteria: this.criteria.value,
          Tags: this.arkTags
        });
      }
    }
  }

  /**
   *
   * Seleccionar una etiqueta para agregar al array de búsqueda
   * @param tag Etiqueta seleccionada para agregar al array
   */
  addTags(tag: string) {
    this.arkTags.push(tag);
  }

  /**
   * Se ejecuta la búsqueda cuando se presiona enter
   * @param event Evento de tipo keyboard
   */
  keyDownFunction(event: KeyboardEvent) {
    if (event.key.toLowerCase() === 'enter') {
      this.criteriaSearch();
      event.stopPropagation();
    }
  }

  criteriaSearch() {
    this.searching.emit({
      Criteria: this.criteria.value,
      Tags: this.arkTags.map((tag) => tag.text)
    });
  }

  /**
   * Limpia el criteria y emite una nueva búsqueda
   */
  onCriteriaClearAndEmit() {
    this.criteria.setValue('');
    this.searching.emit({
      Criteria: this.criteria.value,
      Tags: this.arkTags.map((tag) => tag.text)
    });
  }

  /**
   * Cambia entre tabla/grid y emite el cambio
   */
  toggleCardListActive() {
    this.toggleCardList = !this.toggleCardList;
    this.toggledCardListActive.emit(this.toggleCardList);
  }

  /**
   * Emite evento con el filtro/orden seleccionado
   * @param sort Filtro seleccionado
   */
  onSearchSortOrder(sort: SortBy) {
    if (sort.Active) {
      this.sortActive = sort.Active = false;
      this.filterSort.emit(sort);
      return;
    }
    this.sortBy.forEach((currentSort) => (currentSort.Active = false));
    this.sortActive = sort.Active = true;
    this.filterSort.emit(sort);
  }

  onActiveFilterCustom() {
    this.onActiveFilterCustomInput = !this.onActiveFilterCustomInput;
    if (!this.onActiveFilterCustomInput) {
      this.selectedCustomFilter.emit(null);
    }
  }

  /**
   * Emite evento con la opción seleccionada
   * @param option Opción seleccionada de tipo OptionList
   */
  onOptionsSelected(option: OptionsList) {
    this.optionSelected.emit(option.Key);
  }

  selectedTag(tags: any[]): void {
    this.arkTags = tags.map((tag) => tag.text);
    this.searching.emit({
      Criteria: this.criteria.value,
      Tags: this.arkTags.map((tag) => tag.text)
    });
  }

  onFilterCustom(key: any) {
    this.selectedCustomFilter.emit(key);
  }

  hasAnyPermission(permissions: string[]) {
    if (!permissions || permissions.length < 1) {
      return true;
    }
    return Object.keys(this.permissions).some((key) =>
      permissions.some(
        (permission) => permission === key && this.permissions[key] === true
      )
    );
  }

  initTags() {
    const title = this.translateService.instant('general.Tags');
    this.filteredTagsOptions = {
      title,
      serverSide: true,
      processResult: (item: List<{ Name: string }>) => ({
        results: item.Data.map((obj: any) => ({
          text: obj.Name
        }))
      }),
      allowPagination: true,
      url: `${this.baseApiUrlService.baseApiUrl}/api/tags/`
    };
  }

  initGeneralTags() {
    const title = this.translateService.instant('general.Tags');
    this.filteredGeneralTagsOptions = {
      title,
      serverSide: true,
      allowMultipleSelection: true,
      processResult: (item: List<{ Name: string }>) => ({
        results: item.Data.map((obj: any) => ({
          text: obj.Name
        }))
      }),
      allowPagination: true,
      url: `${this.baseApiUrlService.baseApiUrl}/api/tags/`
    };
  }

  initGeneral() {
    this.filteredGeneralOptions = {
      serverSide: true,
      processResult: (items: any) => ({
        results: items.Data.map(
          (obj: {
            Id: string;
            Name: string;
            Description: string;
            Title: string;
            FirstName: string;
            column1: string;
            Version: string;
            DataSourceName: string;
            PublicId: number;
          }) => ({
            id: obj.Id,
            text:
              obj.Name ||
              obj.Description ||
              obj.Title ||
              obj.FirstName ||
              obj.column1 ||
              obj.DataSourceName ||
              obj.Version,
            publicId: `SC-${obj.PublicId}`
          })
        )
      }),
      allowPagination: true,
      url: `${this.baseApiUrlService.baseApiUrl}/${this.apiComponent}`
    };
  }

  activateCustomFiltersTable() {
    if (this.customFiltersTableService.activateFilters) {
      this.customFilterTableComponent.hideFilters();
      return;
    }
    this.customFilterTableComponent.onActivateFilter();
  }

  activateCustomFilterChips() {
    if (!this.showFilterByChips) {
      this.showFilterByChips = true;
    } else {
      this.showFilterByChips = false;
    }
  }
}
